import { navigate } from "wouter/use-browser-location";

/**
 * Routes the app when a push notification is tapped.
 *
 * The FCM service worker is scoped away from "/" (the PWA worker owns that
 * scope), so it controls no pages and client.navigate() rejects — tapping a
 * notification used to just focus the app on whatever page was open. Instead:
 *
 *  - Running app: the SW posts { type: 'notification-navigate', url } and the
 *    page routes itself here.
 *  - Cold start: iOS often opens the PWA at start_url and drops the URL passed
 *    to openWindow, so the SW stashes the target in the Cache API and this
 *    picks it up on boot. Only a fresh stash is honored, so a stale one can't
 *    hijack a normal open later.
 *
 * Deliberately free of any Firebase imports so it can bind at boot without
 * pulling in the (large) messaging bundle.
 */

const INTENT_CACHE = "kw-nav-intent";
const INTENT_KEY = "/pending-navigation";
const INTENT_MAX_AGE_MS = 2 * 60 * 1000;

let bound = false;

export function bindNotificationTapNavigation(): void {
  if (bound || typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  bound = true;

  navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
    const data = event.data;
    if (
      data?.type === "notification-navigate" &&
      typeof data.url === "string" &&
      data.url.startsWith("/")
    ) {
      navigate(data.url);
    }
  });

  void consumePendingNavigation();
}

async function consumePendingNavigation(): Promise<void> {
  if (!("caches" in window)) return;
  try {
    const cache = await caches.open(INTENT_CACHE);
    const res = await cache.match(INTENT_KEY);
    if (!res) return;
    await cache.delete(INTENT_KEY);

    const { url, ts } = await res.json();
    if (
      typeof url === "string" &&
      url.startsWith("/") &&
      Date.now() - (ts || 0) < INTENT_MAX_AGE_MS
    ) {
      navigate(url);
    }
  } catch {
    // A failed read just means no deep link this launch.
  }
}
